import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, FileText, Eye, ShieldCheck } from "lucide-react";
import { API } from "@/lib/api";

export default function FileViewer() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const fileId = params.get("file");
  const token = params.get("t");
  const name = params.get("name") || "document";
  const [blobUrl, setBlobUrl] = useState(null);
  const [contentType, setContentType] = useState("");
  const [error, setError] = useState("");
  const [obscured, setObscured] = useState(false);

  useEffect(() => {
    if (!fileId || !token) {
      navigate("/");
      return;
    }
    let cancelled = false;
    const url = `${API}/files/view/${fileId}?token=${encodeURIComponent(token)}`;
    fetch(url)
      .then(async (r) => {
        if (!r.ok) throw new Error("Unable to load file");
        const ct = r.headers.get("Content-Type") || "";
        const blob = await r.blob();
        if (!cancelled) {
          setContentType(ct);
          setBlobUrl(URL.createObjectURL(blob));
        }
      })
      .catch((e) => !cancelled && setError(e.message));
    return () => {
      cancelled = true;
    };
  }, [fileId, token, navigate]);

  useEffect(() => {
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [blobUrl]);

  // Anti-screenshot deterrents.
  // True 100% prevention is impossible in a browser, but we make it costly:
  // 1. Block Ctrl/Cmd+P, Ctrl/Cmd+S, Ctrl/Cmd+C, PrintScreen
  // 2. Obscure the document when the window loses focus (alt-tab / screenshot tools)
  // 3. Obscure on visibility change (tab switching, system snipping tools)
  // 4. Prevent print stylesheet rendering
  // 5. Disable text selection + context menu (already on the wrapper)
  useEffect(() => {
    const onKey = (e) => {
      const k = (e.key || "").toLowerCase();
      const blocked = (
        (e.ctrlKey || e.metaKey) && ["p", "s", "c", "u", "a"].includes(k)
      ) || k === "printscreen" || k === "f12";
      if (blocked) {
        e.preventDefault();
        e.stopPropagation();
        setObscured(true);
        setTimeout(() => setObscured(false), 1500);
        return false;
      }
    };
    const onBlur = () => setObscured(true);
    const onFocus = () => setObscured(false);
    const onVis = () => setObscured(document.hidden);
    const onBeforePrint = (e) => { e.preventDefault?.(); setObscured(true); };
    window.addEventListener("keydown", onKey, true);
    window.addEventListener("blur", onBlur);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("beforeprint", onBeforePrint);
    return () => {
      window.removeEventListener("keydown", onKey, true);
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("beforeprint", onBeforePrint);
    };
  }, []);

  const isImage = contentType.startsWith("image/");
  const isPDF = contentType === "application/pdf";
  const pdfSrc = blobUrl ? `${blobUrl}#toolbar=0&navpanes=0&statusbar=0` : "";

  return (
    <div
      className="fixed inset-0 flex flex-col select-none"
      style={{ background: "var(--vault-bg)" }}
      data-testid="file-viewer"
      onContextMenu={(e) => e.preventDefault()}
      onDragStart={(e) => e.preventDefault()}
      onCopy={(e) => e.preventDefault()}
    >
      <header
        className="h-16 border-b flex items-center justify-between px-4 sm:px-6"
        style={{ borderColor: "var(--vault-border)", background: "var(--vault-bg)" }}
      >
        <button
          data-testid="close-viewer"
          onClick={() => navigate("/")}
          className="btn-ghost inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back</span>
        </button>

        <div className="flex items-center gap-2 text-[var(--vault-text-muted)] min-w-0">
          <FileText className="w-4 h-4 flex-shrink-0" style={{ color: "var(--vault-gold)" }} />
          <span className="font-mono-vault text-sm truncate max-w-[40vw]" data-testid="viewer-filename">{name}</span>
        </div>

        <div className="flex items-center gap-2">
          <a
            href={`/verify`}
            target="_blank"
            rel="noreferrer"
            data-testid="verify-authenticity-link"
            className="btn-ghost inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-xs"
          >
            <ShieldCheck className="w-3.5 h-3.5" style={{ color: "var(--vault-gold)" }} />
            <span className="hidden sm:inline">Verify</span>
          </a>
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs"
            style={{
              background: "rgba(197,160,89,0.08)",
              border: "1px solid rgba(197,160,89,0.25)",
              color: "var(--vault-gold)",
            }}
            data-testid="view-only-badge"
          >
            <Eye className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">View only</span>
          </div>
        </div>
      </header>

      <main className="flex-1 w-full relative" style={{ background: "var(--vault-surface)" }}>
        {error && (
          <div className="h-full flex items-center justify-center text-[var(--vault-text-muted)]">
            <div className="text-center">
              <p className="font-display text-2xl mb-2">Unable to load file</p>
              <p className="text-sm">{error}</p>
            </div>
          </div>
        )}
        {!error && !blobUrl && (
          <div className="h-full flex items-center justify-center">
            <span className="spin" style={{ borderTopColor: "var(--vault-gold)" }} />
          </div>
        )}
        {!error && blobUrl && (
          <>
            {isImage && (
              <div className="h-full w-full flex items-center justify-center p-4">
                <img
                  src={blobUrl}
                  alt={name}
                  className="max-h-full max-w-full object-contain select-none pointer-events-none"
                  draggable={false}
                />
              </div>
            )}
            {isPDF && (
              <iframe src={pdfSrc} title={name} className="w-full h-full border-0" data-testid="pdf-frame" />
            )}
            {!isImage && !isPDF && (
              <iframe src={blobUrl} title={name} className="w-full h-full border-0 bg-white" data-testid="generic-frame" />
            )}
          </>
        )}

        {/* Obscure overlay — covers the document when the window loses focus,
            the tab is hidden, or the user hits a screenshot/print shortcut. */}
        {obscured && (
          <div
            className="absolute inset-0 z-50 flex flex-col items-center justify-center text-center px-6 fade-up"
            style={{ background: "rgba(10,10,12,0.97)", backdropFilter: "blur(24px)" }}
            data-testid="obscure-overlay"
          >
            <Eye className="w-10 h-10 mb-4" style={{ color: "var(--vault-gold)" }} />
            <p className="font-display text-2xl mb-2">Document hidden</p>
            <p className="text-sm text-[var(--vault-text-muted)] max-w-md">
              For confidentiality, this certificate is not displayed when the window is out of focus,
              when a screenshot shortcut is detected, or when printing is attempted.
              Return to this tab to keep viewing.
            </p>
          </div>
        )}
      </main>

      <style>{`
        @media print {
          body { visibility: hidden !important; }
          body::after {
            content: "Printing the digital certificate is disabled.";
            visibility: visible;
            display: block;
            font: 16px sans-serif;
            margin: 40px;
          }
        }
      `}</style>
    </div>
  );
}
